export default function MiamiDestinationsSection() {
  const stats = [
    {
      value: "62%",
      label: "of Miami requests are Caribbean, Latin America, or cruise departures with under 3 weeks notice",
    },
    {
      value: "1–2 weeks",
      label: "average notice for yacht charter, art events, and family visit trips from Miami",
    },
    {
      value: "Nov–Apr",
      label: "peak travel season — passport renewals spike before Art Basel and spring break",
    },
    {
      value: "24-hour",
      label: "shortest emergency departure window we've accommodated for a Miami business traveler",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "The Caribbean & Cuba",
      context: "The most common destination for Miami travelers — by cruise from PortMiami, by yacht across the Gulf Stream, or by direct flight from MIA to Nassau, San Juan, Montego Bay, Havana, and Santo Domingo. The proximity of both the airport and cruise port makes Caribbean travel an impulse decision. All cruise passengers and most air travelers need valid passports with at least six months remaining validity.",
      urgency: "Cruise and yacht charter embarkation deadlines are absolute — passport issues discovered days before departure require emergency processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Latin America",
      context: "Bogota, Medellin, Cali, Cartagena, Mexico City, Guadalajara, Cancun, Sao Paulo, Rio de Janeiro, Buenos Aires, Santiago, and Lima draw Miami's diverse Latin American communities for family visits and business. MIA has direct service to more Latin American cities than any other U.S. airport. Each country requires six months of passport validity, and family emergencies can require travel within 48–72 hours.",
      urgency: "Family emergencies across Latin America can require travel within 48–72 hours — standard processing is not an option",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Europe & Art Basel Cities",
      context: "Paris, London, Rome, Barcelona, Basel, and Milan attract Miami art professionals for Art Basel, design fairs, gallery openings, and client meetings. The European art fair season (March through December) creates concentrated travel demand. The Schengen Area requires three months of passport validity beyond your planned departure date.",
      urgency: "Art Basel and design fair dates are fixed — passport validity checks should happen three months before each event",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Canada",
      context: "Toronto, Montreal, and Vancouver draw Miami business travelers for finance, healthcare, technology conferences, and art world connections. Canadian ski destinations attract winter sports enthusiasts. Canada requires a valid passport for all air arrivals, and many Miami families travel with minors whose five-year passports expire faster than expected.",
      urgency: "Business conference registration deadlines often require confirmed travel 4–6 weeks in advance",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Asia & Middle East",
      context: "Dubai, Hong Kong, Tokyo, and Singapore are increasingly common destinations for Miami finance professionals, art dealers, and international trade executives. MIA's expanding long-haul service makes these destinations more accessible without connecting through other U.S. hubs. Dubai and many Asian countries require six months of passport validity.",
      urgency: "Long-haul business trips are often confirmed 2–3 weeks in advance — passport validity must be verified before booking",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Mexico",
      context: "Cancun, Riviera Maya, and Cozumel are staple destinations for Miami families and groups. Direct and connecting service from MIA to Mexico City, Guadalajara, and Cancun serves both leisure and business travelers. Mexico requires six months of passport validity for entry, and the spring break booking surge from January through March creates compressed timelines.",
      urgency: "Spring break bookings surge in January — passport discoveries spike in February and March",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Miami Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Miami
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Miami travelers head to the Caribbean by cruise and yacht, Latin America, Europe, Asia, and Canada for leisure cruises, yacht charters, family visits, art events, and international business. These are the destinations that create the most passport urgency for our Miami clients.
          </p>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Destination cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal links */}
        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout South Florida</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Fort Lauderdale", href: "/cities/fort-lauderdale/" },
              { name: "Hollywood", href: "/cities/hollywood/" },
              { name: "Coral Gables", href: "/cities/coral-gables/" },
              { name: "Aventura", href: "/cities/aventura/" },
              { name: "West Palm Beach", href: "/cities/west-palm-beach/" },
              { name: "Boca Raton", href: "/cities/boca-raton/" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}